import React from 'react';
import { useSelector } from 'react-redux';
import { ExternalScripts } from 'mm-ui-components';
import {
  getExternalScripts,
  getCanonicalUrl,
  getCmpId,
  getAuthorIds,
  getProperty,
  getPageType,
} from '../../store/config/config.selectors';
import { ExternalScripts as ExternalScriptsType } from '../../store/config/config.reducer';
import { getCWVScript } from '../../utilities/getCWVScript';
import { shouldBlockAds } from '../../utilities/experiments/fansidedBlockAds';

export const CMP_EXCLUDED_PROPERTIES = [''];

const ONE_TRUST_DEFAULT_ID = '01948883-cdc5-73d6-a267-dd1f0a86d169';

function getOneTrustScripts(cmpId: string | null): ExternalScriptsType {
  const oneTrustID = cmpId || ONE_TRUST_DEFAULT_ID;

  const oneTrustScriptAutoBlock = {
    type: 'URL' as const,
    source: `https://cdn-ukwest.onetrust.com/consent/${oneTrustID}/OtAutoBlock.js`,
    name: 'OTAutoBlock',
    attributes: null,
    loadingType: 'blocking' as const,
  };

  const oneTrustScriptURL = {
    type: 'URL' as const,
    source: 'https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js',
    name: 'OTURL',
    attributes: {
      'data-domain-script': oneTrustID,
    },
    loadingType: 'blocking' as const,
  };

  const oneTrustScriptIAB = {
    type: 'URL' as const,
    source: 'https://cdn.cookielaw.org/opt-out/otCCPAiab.js',
    name: 'OTIAB',
    attributes: {
      'ccpa-opt-out-ids': 'C0004' as const,
      'ccpa-opt-out-geo': 'ca' as const,
      'ccpa-opt-out-lspa': 'true' as const,
    },
    loadingType: 'blocking' as const,
  };

  const oneTrustScriptInit = {
    type: 'code' as const,
    source: 'function OptanonWrapper() {var geoData = OneTrust.getGeolocationData();if (geoData && geoData.country === "US") {OneTrust.OnConsentChanged(function(eventData) {location.reload();});}}',
    name: 'OTInit',
    attributes: null,
    loadingType: 'blocking' as const,
  };

  return [oneTrustScriptAutoBlock, oneTrustScriptURL, oneTrustScriptIAB, oneTrustScriptInit];
}

export const HeadScripts: React.FunctionComponent = () => {
  const scripts = useSelector(getExternalScripts);
  const canonicalUrl = useSelector(getCanonicalUrl);
  const cmpId = useSelector(getCmpId);
  const property = useSelector(getProperty);
  const authorIds = useSelector(getAuthorIds);
  const pageType = useSelector(getPageType);

  const isPropertyExcluded = CMP_EXCLUDED_PROPERTIES.includes(property);
  if (!isPropertyExcluded) {
    scripts.unshift(...getOneTrustScripts(cmpId));
  }

  // Test: block ads for some fansided properties
  if (shouldBlockAds(property)) {
    const clientApiData = JSON.stringify({ pageData: { tags: ['no-ads'] } });
    scripts.push({
      type: 'code',
      source: `window.clientApiData = ${clientApiData};`,
      name: 'clientApiData',
      attributes: null,
      loadingType: 'async',
    });
  }

  scripts.push({
    type: 'code',
    source: `
    window.mmBCPageData = {
      property: '${property}',
      authors: ${JSON.stringify(authorIds)},
      pageType: '${pageType}',
    };`,
    name: 'mmBCPageData',
    attributes: null,
    loadingType: 'blocking',
  });

  // Add BlueConic script for all properties on www.si.com
  if (canonicalUrl && canonicalUrl.includes('www.si.com')) {
    scripts.push({
      type: 'URL',
      source: 'https://p329.si.com/script.js',
      name: 'BlueConic',
      attributes: null,
      loadingType: 'async',
    });
  }

  if (canonicalUrl) {
    const cwvScript = getCWVScript(canonicalUrl);
    if (cwvScript) {
      scripts.push(cwvScript);
    }
  }

  return <ExternalScripts scripts={scripts} />;
};
